import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Box, Building, Check, ChevronDown, CreditCard, Crown, LogOut, Menu, Plus, Search, Settings } from "lucide-react";
import { ROLE_LABELS } from "@digisubs/shared";
import { useAuth } from "@/app/auth";
import { api } from "@/lib/api";
import { relative } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useRealtime } from "@/lib/socket";
import { Avatar, DropdownMenu, EmptyState, IconButton, Popover, useToast } from "@/components/ui";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  read: boolean;
}

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

function ClientSwitcher() {
  const { me, switchClient, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const current = me?.current;
  if (!me || !current) return null;
  const orgClients = me.organizations.find((o) => o.id === current.orgId)?.clients.length ?? 1;

  return (
    <Popover
      placement="bottom-start"
      className="w-[320px] p-2"
      trigger={
        <button type="button" className="group flex min-w-0 items-center gap-3 rounded-xl px-1 py-1 text-left">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-[#1c2742] bg-[#0d1528] text-[#d5dcea] group-hover:border-[#2a3858]">
            <Box className="size-5" strokeWidth={1.6} />
          </span>
          <span className="hidden min-w-0 max-w-[230px] leading-tight min-[480px]:block xl:max-w-[360px]">
            <span className="block truncate text-[13px] text-[#dfe5f1]">DigiSubs - Business OS</span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[14px] font-semibold text-white">
              <span className="truncate">{orgClients > 1 ? `${current.orgName} · ${current.clientName}` : current.orgName}</span>
              <ChevronDown className="size-4 shrink-0 text-[#b6c0d6]" />
              <span className="ml-3 hidden whitespace-nowrap text-[12.5px] font-medium text-[#7c74ff] group-hover:text-[#9d97ff] xl:inline">Changer de client</span>
            </span>
          </span>
        </button>
      }
    >
      {(close) => (
        <div className="max-h-[70vh] overflow-y-auto">
          {me.organizations.map((org) => (
            <div key={org.id} className="mb-1">
              <p className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted">{org.name}</p>
              {org.clients.map((client) => {
                const active = client.id === current.clientId;
                return (
                  <button
                    key={client.id}
                    type="button"
                    onClick={async () => {
                      close();
                      if (active) return;
                      try {
                        await switchClient(client.id);
                        navigate("/");
                        toast.success(`Client actif : ${client.name}`);
                      } catch (err) {
                        toast.fromError(err, "Changement de client impossible");
                      }
                    }}
                    className={cn("flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px]", active ? "bg-primary/12 text-white" : "text-ink hover:bg-panel-3")}
                  >
                    <Avatar name={client.name} src={client.logoUrl} size="xs" square />
                    <span className="min-w-0 flex-1 truncate">{client.name}</span>
                    <span className="text-[11px] text-muted">{client.currency}</span>
                    {active ? <Check className="size-4 text-primary-400" /> : null}
                  </button>
                );
              })}
            </div>
          ))}
          {can("org.manage") ? (
            <button
              type="button"
              onClick={() => {
                close();
                navigate("/organisation?tab=clients&nouveau=1");
              }}
              className="mt-1 flex w-full items-center gap-2 rounded-lg border-t border-line px-2.5 py-2.5 text-[13px] text-primary-400 hover:bg-panel-3"
            >
              <Plus className="size-4" /> Ajouter un client
            </button>
          ) : null}
        </div>
      )}
    </Popover>
  );
}

function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useAuth();
  const query = useQuery({
    queryKey: ["notifications", me?.current?.clientId],
    queryFn: () => api.get<{ unread: number; items: NotificationItem[] }>("/notifications"),
    refetchInterval: 120_000,
    enabled: Boolean(me?.current),
  });
  useRealtime("notification.created", () => void qc.invalidateQueries({ queryKey: ["notifications"] }));
  const markAll = async () => {
    await api.post("/notifications/read", {});
    void qc.invalidateQueries({ queryKey: ["notifications"] });
  };
  return (
    <Popover
      placement="bottom-end"
      className="w-[min(380px,calc(100vw-24px))] p-0"
      trigger={
        <IconButton label="Notifications" badge={query.data?.unread ?? 0}>
          <Bell className="size-5" />
        </IconButton>
      }
    >
      {(close) => (
        <div>
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="text-sm font-semibold">Notifications</p>
            {query.data?.unread ? (
              <button type="button" onClick={markAll} className="text-xs text-primary-400 hover:text-primary-300">
                Tout marquer comme lu
              </button>
            ) : null}
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-1.5">
            {!query.data?.items.length ? (
              <EmptyState compact icon={<Bell className="size-5" />} title="Aucune notification" description="Les alertes importantes de votre activité apparaîtront ici." />
            ) : (
              query.data.items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={async () => {
                    close();
                    if (!n.read) await api.post("/notifications/read", { ids: [n.id] }).catch(() => undefined);
                    void qc.invalidateQueries({ queryKey: ["notifications"] });
                    if (n.link) navigate(n.link);
                  }}
                  className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-panel-3"
                >
                  <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-primary")} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-ink">{n.title}</span>
                    {n.body ? <span className="mt-0.5 block text-[12.5px] text-muted">{n.body}</span> : null}
                    <span className="mt-1 block text-[11px] text-faint">{relative(n.createdAt)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}

function UserMenu() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const items = useMemo(
    () => [
      { label: "Réglages du compte", icon: <Settings />, onSelect: () => navigate("/reglages") },
      { label: "Organisation", icon: <Building />, onSelect: () => navigate("/organisation") },
      { label: "Abonnement", icon: <CreditCard />, onSelect: () => navigate("/abonnement") },
      ...(me?.user.isPlatformAdmin ? [{ label: "Admin plateforme", icon: <Crown />, onSelect: () => navigate("/admin") }] : []),
      { label: "", divider: true },
      {
        label: "Se déconnecter",
        icon: <LogOut />,
        danger: true,
        onSelect: async () => {
          await logout();
          navigate("/connexion", { replace: true });
        },
      },
    ],
    [me, navigate, logout],
  );
  if (!me) return null;
  return (
    <DropdownMenu
      items={items}
      trigger={
        <button type="button" className="flex items-center gap-2.5 rounded-xl py-1 pl-1 pr-2 hover:bg-panel-2">
          <Avatar name={me.user.name} src={me.user.avatarUrl} size="md" gradient="from-[#2563eb] to-[#3b82f6]" />
          <span className="hidden min-w-0 text-left leading-tight md:block">
            <span className="block max-w-[170px] truncate text-[13.5px] font-semibold text-white">{me.user.name}</span>
            <span className="block text-[11.5px] text-[#93a0bb]">{me.current ? ROLE_LABELS[me.current.role] : ""}</span>
          </span>
          <ChevronDown className="hidden size-4 text-muted md:block" />
        </button>
      }
    />
  );
}

export function Topbar({ onOpenNav, onOpenSearch }: { onOpenNav: () => void; onOpenSearch: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-canvas/85 px-3 backdrop-blur-md sm:px-5">
      <IconButton label="Ouvrir le menu" className="lg:hidden" onClick={onOpenNav}>
        <Menu className="size-5" />
      </IconButton>
      <div className="min-w-0 shrink">
        <ClientSwitcher />
      </div>
      <div className="flex min-w-0 flex-1 justify-center">
        <button
          type="button"
          onClick={onOpenSearch}
          className="hidden h-10 w-full max-w-[560px] items-center gap-2.5 rounded-xl border border-[#1c2742] bg-[#0b1222] px-3.5 text-left text-[13px] text-[#7f8ba6] transition-colors hover:border-[#2c3a5c] md:flex"
        >
          <Search className="size-4 shrink-0 text-[#a3aec6]" />
          <span className="min-w-0 flex-1 truncate">Rechercher des clients, produits, commandes, conversations...</span>
          <kbd className="shrink-0 rounded-md bg-[#18223a] px-2 py-0.5 font-sans text-[11px] font-semibold text-[#aab5cc]">{isMac() ? "⌘K" : "Ctrl K"}</kbd>
        </button>
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
        <IconButton label="Rechercher" className="md:hidden" onClick={onOpenSearch}>
          <Search className="size-5" />
        </IconButton>
        <Notifications />
        <UserMenu />
      </div>
    </header>
  );
}
